import { FC } from 'react';
import { LineItem } from 'shared/types/cart';
import { Money } from 'shared/types/product';
import Typography from 'components/commercetools-ui/atoms/typography';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useI18n from 'helpers/hooks/useI18n';

interface ItemPriceProps {
  item: LineItem;
  layout?: 'horizontal' | 'vertical';
  /** Cart rows: show quantity × unit price, with the unit price beneath once quantity exceeds 1. */
  showLineTotal?: boolean;
}

/**
 * The price to show for the row, struck through against list price when discounted.
 *
 * `discountedPrice` only carries *product* discounts (price.discounted). Item-level *cart*
 * discounts (e.g. a 100%-off samples code) live in the per-unit discount portions instead, so
 * without this derivation a fully discounted row would still show its list price.
 *
 * Portions are read from the quantity-aware buckets, and only when exactly one bucket covers the
 * whole line quantity: with multiple or partial buckets, different units carry different per-unit
 * discounts, so no single unit price is truthful — the row falls back to list price rather than
 * showing a wrong (often $0) figure.
 * (`item.totalPrice / count` is not a substitute: merged finish rows recompute totalPrice without
 * cart discounts, so it would erase the strikethrough on exactly the rows this exists for.)
 */
export const effectiveDiscountedPrice = (item: LineItem) => {
  if (item.discountedPrice) return item.discountedPrice;

  const buckets = item.discountedPricePerCount ?? [];
  if (buckets.length !== 1 || buckets[0].count !== item.count || !item.price) return undefined;

  const cartDiscountPerUnit = cartDiscountPerUnitOf(buckets[0]);
  if (cartDiscountPerUnit <= 0) return undefined;

  return { ...item.price, centAmount: Math.max(0, (item.price.centAmount || 0) - cartDiscountPerUnit) };
};

const timesCount = (money: Money, count: number): Money => ({ ...money, centAmount: (money.centAmount || 0) * count });

const cartDiscountPerUnitOf = (bucket: NonNullable<LineItem['discountedPricePerCount']>[number]) =>
  (bucket.discounts ?? []).reduce((acc, discount) => acc + (discount.discountedAmount?.centAmount || 0), 0);

/**
 * The discounted amount for the whole row, including when units carry different cart discounts
 * (e.g. 3 × $10 with one free and two at $7.50 → $15). Summed from the per-count buckets, with any
 * units no bucket covers at list price, for the same reason effectiveDiscountedPrice avoids
 * `item.totalPrice`. Undefined when nothing on the row is discounted.
 */
const discountedLineTotal = (item: LineItem): Money | undefined => {
  const count = item.count ?? 1;
  if (item.discountedPrice) return timesCount(item.discountedPrice, count);
  if (!item.price) return undefined;

  const unitList = item.price.centAmount || 0;
  const buckets = item.discountedPricePerCount ?? [];
  const covered = buckets.reduce((acc, bucket) => acc + (bucket.count || 0), 0);
  const centAmount =
    buckets.reduce(
      (acc, bucket) => acc + (bucket.count || 0) * Math.max(0, unitList - cartDiscountPerUnitOf(bucket)),
      0,
    ) +
    Math.max(0, count - covered) * unitList;

  if (centAmount >= unitList * count) return undefined;
  return { ...item.price, centAmount };
};

/**
 * The price every unit on the row pays, or undefined when discounts differ by unit — in which case
 * no single "each" figure is truthful and callers should omit it.
 */
export const uniformUnitPrice = (item: LineItem): Money | undefined => {
  const discountedUnit = effectiveDiscountedPrice(item);
  if (discountedUnit) return discountedUnit;
  return discountedLineTotal(item) ? undefined : item.price;
};

const ItemPrice: FC<ItemPriceProps> = ({ item, layout = 'horizontal', showLineTotal = false }) => {
  const { locale } = useI18n();

  if (item.isGift) {
    return (
      <Typography translation={{ id: 'gift', file: 'cart' }} className="text-14 font-normal">
        Gift
      </Typography>
    );
  }

  const listPrice = item.price ?? { centAmount: 0 };
  const count = item.count ?? 1;
  // Cart rows show the whole line; elsewhere (order history, design totals) the unit figure. A line
  // whose units carry different discounts still gets a truthful total, just no "each" note.
  const discountedPrice = showLineTotal ? discountedLineTotal(item) : effectiveDiscountedPrice(item);
  const shownPrice = discountedPrice ?? (showLineTotal ? timesCount(listPrice, count) : listPrice);
  const shownListPrice = showLineTotal ? timesCount(listPrice, count) : listPrice;
  const unitPrice = showLineTotal && count > 1 ? uniformUnitPrice(item) : undefined;
  const unitPriceNote = unitPrice && (
    <Typography className="text-12 font-normal text-neutral-black-400">
      {`${CurrencyHelpers.formatForCurrency(unitPrice, locale)} each`}
    </Typography>
  );

  if (discountedPrice) {
    return (
      <div className={`flex flex-col ${layout === 'vertical' ? 'items-end' : ''}`}>
        <div className={layout === 'vertical' ? 'flex flex-col items-end gap-1' : 'flex items-center gap-5'}>
          <Typography className="body-text font-bold leading-6 text-primary">
            {CurrencyHelpers.formatForCurrency(shownPrice, locale)}
          </Typography>
          <Typography className="body-text font-normal leading-loose text-tertiary line-through">
            {CurrencyHelpers.formatForCurrency(shownListPrice, locale)}
          </Typography>
        </div>
        {unitPriceNote}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <Typography className="body-text font-bold leading-6">
        {CurrencyHelpers.formatForCurrency(shownPrice, locale)}
      </Typography>
      {unitPriceNote}
    </div>
  );
};

export default ItemPrice;
